import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ActiveFaultCodesWidget,
  Connectivity,
  EngineLoad,
  FuelConsumption,
  PentaDashboard,
  UnitMap,
} from '@volvogroup-internal/dashboard';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { BING_API_KEY } from '@/config/EnviromentConfig';
import { getToken } from '@/config/MsalAuthConfig';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { getAllFleetUnitsQueryOptions } from '@/service/vpc/queryOptions';
import { client } from '@/service/vpc/vpcService';
import { cn } from '@/utils/cn';

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/dashboard/')({
  component: DashboardPage,
  errorComponent: ErrorBoundary,
  loader: async (opts) => {
    const { fleetId } = opts.params;
    const units = await opts.context.queryClient.ensureQueryData(getAllFleetUnitsQueryOptions({ fleetId }));
    return units;
  },
});

function DashboardPage() {
  const { fleetId } = useParams({
    from: '/_authenticated/_account/fleets/$fleetId/dashboard',
  });
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { fleetVerifiedUnits, isMarineAccount } = useFleetAccount();

  const { data: fleetUnits } = useQuery(getAllFleetUnitsQueryOptions({ fleetId }));

  const fleetUnitsIds = fleetUnits?.items.map((unit) => Number(unit.endCustomerUnitId));
  const filteredUnits = fleetVerifiedUnits?.filter((unit) => fleetUnitsIds?.includes(unit.endCustomerUnitId));
  const endCustomerUnitIds = filteredUnits.map((unit) => unit.endCustomerUnitId);

  return (
    <PentaDashboard
      client={{
        httpClient: {
          instance: client,
          getToken: getToken,
        },
        queryClient,
      }}
      language={i18n.language}
      preFilterUnits={endCustomerUnitIds}
      showFilterBar={true}
      showFilterMenu={true}
    >
      <div className="grid grid-cols-12 gap-5">
        <Link
          to="/fleets/$fleetId/dashboard/active-fault-codes"
          params={{ fleetId }}
          className="col-span-full flex flex-col items-stretch md:col-span-6"
        >
          <ActiveFaultCodesWidget />
        </Link>
        <Link
          to="/fleets/$fleetId/dashboard/connectivity"
          params={{ fleetId }}
          className="col-span-full flex flex-col items-stretch md:col-span-6"
        >
          <Connectivity endCustomerUnitIds={endCustomerUnitIds} />
        </Link>
        <div className="col-span-full flex flex-col items-stretch md:col-span-4">
          <EngineLoad />
        </div>
        {!isMarineAccount && (
          <div className="col-span-full flex flex-col items-stretch md:col-span-8">
            <UnitMap
              defaultZoom={16}
              bingApiKey={BING_API_KEY}
              navigate={(_, endCustomerUnitId) => {
                if (!endCustomerUnitId) return;
                navigate({
                  to: '/fleets/$fleetId/units/$endCustomerUnitId',
                  params: { fleetId, endCustomerUnitId },
                });
              }}
            />
          </div>
        )}
        <Link
          to="/fleets/$fleetId/dashboard/fuel-consumption"
          params={{ fleetId }}
          className={cn('col-span-full flex flex-col items-stretch md:col-span-8', {
            'md:col-span-12': !isMarineAccount,
          })}
        >
          <FuelConsumption type="yearly" />
        </Link>
      </div>
    </PentaDashboard>
  );
}
